import path from 'path'
import { Box, Stack, Typography } from '@mui/material'
import { Variant } from '@mui/material/styles/createTypography'
import { AppV1AppEntitlementsowner } from 'c1gen/roles'
import { AppAvatar } from 'components/core/avatar'
import { DisplayNameDetails } from 'components/core/display-name-details'
import { EntitlementAvatar } from 'components/core/display-name-details/display'
import { useHasRole } from 'components/no-ui/capability'
import { EntitlementTooltipContent } from 'components/pages/entitlement/tooltip/TooltipEntitlement'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { SystemAnyAccessEntitlementID } from 'lib/constants/system'
import { routes, unsafeRoute, Route } from 'lib/routes'
import React, { useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'

type EntitlementDisplayTitleProps = {
    firstStr?: string
    secondStr?: string
    parentWidth?: number
    separator?: string
    titleVariant?: Variant
}

export const EntitlementDisplayTitle = ({
    firstStr,
    secondStr,
    titleVariant,
    parentWidth,
    separator = '▸',
}: EntitlementDisplayTitleProps): React.ReactElement => {
    const part1Ref = useRef<HTMLSpanElement>(null)
    const part2Ref = useRef<HTMLSpanElement>(null)
    const [part1Width, setPart1Width] = React.useState<number | undefined>(undefined)
    const [part2Width, setPart2Width] = React.useState<number | undefined>(undefined)

    const splitByRatio = (total: number, ratio1: number, ratio2: number): [number, number] => {
        const totalRatio = ratio1 + ratio2
        const part1 = (ratio1 / totalRatio) * total
        const part2 = (ratio2 / totalRatio) * total

        return [part1, part2]
    }

    useEffect(() => {
        if (!part1Ref.current || !part2Ref.current || !parentWidth) {
            return
        }

        const separatorWidth = 6
        const minWidth = 100
        const parentWidthWithoutAvatar = Math.ceil(parentWidth) - 34 // 34 is the width of the avatar
        const p1RenderedWidth = Math.ceil(part1Ref.current.getBoundingClientRect().width)
        const p2RenderedWidth = Math.ceil(part2Ref.current.getBoundingClientRect().width)

        // If both parts fit in the parent width, no need to adjust
        if (p1RenderedWidth + p2RenderedWidth <= parentWidthWithoutAvatar) {
            return
        }

        // If one part is too short, just render it and adjust the other part
        if (p1RenderedWidth < minWidth) {
            setPart1Width(p1RenderedWidth)
            setPart2Width(parentWidthWithoutAvatar - p1RenderedWidth - separatorWidth)
            return
        }
        if (p2RenderedWidth < minWidth) {
            setPart1Width(parentWidthWithoutAvatar - p2RenderedWidth - separatorWidth)
            setPart2Width(p2RenderedWidth)
            return
        }

        // Both parts are long enough, adjust them by ratio
        const [p1Width, p2Width] = splitByRatio(parentWidthWithoutAvatar, p1RenderedWidth, p2RenderedWidth)

        // If one part is too short after adjusting, set it to the minimum width and adjust the other part
        if (p1Width < minWidth) {
            const widthToAdd = minWidth - p1Width
            setPart1Width(minWidth)
            setPart2Width(p2Width - widthToAdd - separatorWidth)
            return
        }
        if (p2Width < minWidth) {
            const widthToAdd = minWidth - p2Width
            setPart1Width(p1Width - widthToAdd - separatorWidth)
            setPart2Width(minWidth)
            return
        }

        // Both parts are long enough after adjusting
        setPart1Width(p1Width - separatorWidth)
        setPart2Width(p2Width)
    }, [secondStr, parentWidth, firstStr])

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            {firstStr && <Typography noWrap ref={part1Ref} variant={titleVariant} sx={{ lineHeight: 'normal', maxWidth: part1Width ? `${part1Width}px` : 'auto', color: 'inherit' }}>
                {firstStr}
            </Typography>}
            {firstStr && secondStr && <Typography sx={{ lineHeight: 'normal', color: 'inherit' }} noWrap variant={titleVariant}>{separator}</Typography>}
            {secondStr && <Typography noWrap ref={part2Ref} variant={titleVariant} sx={{ lineHeight: 'normal', maxWidth: part2Width ? `${part2Width}px` : 'auto', color: 'inherit' }} title={secondStr}>
                {secondStr}
            </Typography>}
        </Stack>
    )
}

export type C1EntitlementDisplayNameProps = {
    entitlement: AppEntitlementValue
    displayNameVariant?: Variant
    drawerContent?: React.ReactElement
    excludeDetails?: boolean
    readOnly?: boolean
    entitlementPageTabDeepLink?: string
    textTruncationWidth?: string
    showTooltip?: boolean
    forceShowHiddenSlugs?: boolean
}

const hiddenSlugs = new Set(['member', 'access', 'assignment', 'enrollment', 'assigned'])

export const urlFromAppEntitlement = (appId: string, entitlementId: string): Route => routes.applications.entitlement(appId, entitlementId)
export const C1EntitlementDisplayName = ({
    entitlement,
    drawerContent,
    displayNameVariant,
    entitlementPageTabDeepLink,
    excludeDetails: _excludeDetails = false,
    readOnly = false,
    textTruncationWidth = '100%',
    showTooltip,
    forceShowHiddenSlugs = false,
}: C1EntitlementDisplayNameProps): React.ReactElement => {
    const intl = useIntl()
    let link: Route = urlFromAppEntitlement(entitlement.appId, entitlement.id)
    const isOwner = useHasRole(AppV1AppEntitlementsowner)

    if (entitlementPageTabDeepLink) {
        link = unsafeRoute(path.join(link, entitlementPageTabDeepLink))
    }

    const parentRef = useRef<HTMLDivElement>(null)
    const [parentWidth, setParentWidth] = React.useState<number | undefined>(undefined)

    const hasAppResourceDisplayName = !!entitlement.appResource?.displayName
    const hasSlug = !!entitlement.slug

    const hideSlug = forceShowHiddenSlugs ? false : hiddenSlugs.has(entitlement.slug)

    // TODO @degzhaus: remove this after catalog => profile data migration is complete
    const entitlementAppResourceTypeDisplayName = entitlement.appResourceType?.displayName.toLowerCase() === 'catalog' ? 'Access profile' : entitlement.appResourceType?.displayName

    useEffect(() => {
        if (!parentRef.current) {
            return
        }

        setParentWidth(parentRef.current.getBoundingClientRect().width)
    }, [])

    const isAppAccessEntitlement = entitlement.id === SystemAnyAccessEntitlementID

    const excludeDetails = isAppAccessEntitlement || _excludeDetails

    const { firstStr, secondStr } = useMemo(() => {
        let first: string | undefined
        let second: string | undefined

        if (hasAppResourceDisplayName) {
            first = entitlement.appResource?.displayName
        } else if (hasSlug) {
            first = entitlement.displayName
        }

        if (isAppAccessEntitlement) {
            first = intl.formatMessage({ id: 'entitlement_app_access', defaultMessage: '{app} app access' }, { app: entitlement.app?.displayName })
        }

        if (!hideSlug && !isAppAccessEntitlement) {
            second = hasSlug ? entitlement.slug : entitlement.displayName
        }

        return { firstStr: first, secondStr: second }
    }, [entitlement, hasAppResourceDisplayName, hasSlug, hideSlug, isAppAccessEntitlement, intl])

    const avatar = entitlement.appResourceType
        ? <EntitlementAvatar
            type={entitlement.appResourceType.displayName}
            appAvatar={<AppAvatar appId={entitlement.appId} appName={entitlement.app?.displayName} decorative />}
            decorative
        />
        : <AppAvatar appId={entitlement.appId} appName={entitlement.app?.displayName} size="xs" decorative />

    const displayNameElement = (
        <EntitlementDisplayTitle
            parentWidth={parentWidth ? parentWidth - (entitlement.deletedAt ? 60 : 0) : undefined}
            firstStr={firstStr}
            secondStr={secondStr}
            titleVariant={displayNameVariant}
        />
    )

    const details = !excludeDetails ? (
        <EntitlementDisplayTitle
            parentWidth={parentWidth}
            firstStr={entitlement.app?.displayName}
            secondStr={entitlementAppResourceTypeDisplayName || ''}
            titleVariant="caption"
            separator="·"
        />
    ) : undefined

    const tooltipContent = showTooltip && isOwner ? (
        <EntitlementTooltipContent entitlement={entitlement} />
    ) : undefined

    return (
        <Box ref={parentRef} sx={{ maxWidth: textTruncationWidth }}>
            <DisplayNameDetails
                avatar={avatar}
                displayName="" // because we want to pass a truncated ReactNode, not a string
                displayNameVariant={displayNameVariant}
                displayNameMetadata={displayNameElement}
                details={details}
                link={readOnly ? undefined : link}
                drawerContent={drawerContent}
                drawerVariant="sm"
                readOnly={readOnly}
                isDeleted={!!entitlement.deletedAt}
                showTooltip={showTooltip && isOwner}
                tooltipContent={tooltipContent}
                margin={false}
                maxWidth={textTruncationWidth}
                tooltipProps={{
                    placement: 'right-end',
                    PopperProps: { sx: { '& .MuiTooltip-tooltip': { maxWidth: 'none' } } },
                }}
            />
        </Box>
    )
}
